import { apiService } from '../../../components/api/apiService';
import { logAiModel } from './aiModelLogger';
import {
  AiModelClassificationLabel,
  AiModelCredentialsInput,
  AiModelCredentialsMask,
  AiModelDataType,
  AiModelEnvironment,
  AiModelFormState,
  AiModelHttpMethod,
  AiModelInputField,
  AiModelModality,
  AiModelOutputField,
  AiModelPayloadFormat,
  AiModelProvider,
  AiModelStatus,
  AiModelTask,
  AiModelTestCheck,
  AiModelTestResult,
  AiRegisteredModel,
  CredentialHint,
  InferenceMode,
} from './aiModelTypes';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/ai-models';

function errorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string; detail?: unknown }; message?: string }; status?: number };
    message?: string;
  };
  const code = axiosErr.code || '';
  const raw = axiosErr.message || '';
  if (code === 'ERR_NETWORK' || raw === 'Network Error' || /ECONNREFUSED/i.test(raw)) {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014. Start it with `python run.py` from BackendPYApp.';
  }
  const detail = axiosErr.response?.data?.error?.detail;
  if (Array.isArray(detail) && detail.length && typeof detail[0] === 'string') {
    return `${axiosErr.response?.data?.error?.message || 'Request failed'}: ${detail.join(', ')}`;
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'AI model request failed'
  );
}

function asString(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value);
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asNumber(value: unknown, fallback: number): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? '').trim()).filter(Boolean);
}

function asHint(value: unknown): CredentialHint {
  const row = (value || {}) as Record<string, unknown>;
  return { set: asBool(row.set), hint: asString(row.hint) };
}

function emptyHint(): CredentialHint {
  return { set: false, hint: '' };
}

export function emptyCredentials(): AiModelCredentialsInput {
  return {
    hfToken: '',
    endpointUrl: '',
    apiKey: '',
    scoringUri: '',
    deploymentName: '',
    awsAccessKeyId: '',
    awsSecretAccessKey: '',
    awsSessionToken: '',
    databricksToken: '',
    bearerToken: '',
  };
}

export function emptyCredentialMask(): AiModelCredentialsMask {
  return {
    configured: false,
    hfToken: emptyHint(),
    endpointUrl: emptyHint(),
    apiKey: emptyHint(),
    scoringUri: emptyHint(),
    deploymentName: emptyHint(),
    awsAccessKeyId: emptyHint(),
    awsSecretAccessKey: emptyHint(),
    awsSessionToken: emptyHint(),
    databricksToken: emptyHint(),
    bearerToken: emptyHint(),
  };
}

function mapCredentialsMask(raw: unknown): AiModelCredentialsMask {
  const row = (raw || {}) as Record<string, unknown>;
  return {
    configured: asBool(row.configured),
    hfToken: asHint(row.hfToken ?? row.hf_token),
    endpointUrl: asHint(row.endpointUrl ?? row.endpoint_url),
    apiKey: asHint(row.apiKey ?? row.api_key),
    scoringUri: asHint(row.scoringUri ?? row.scoring_uri),
    deploymentName: asHint(row.deploymentName ?? row.deployment_name),
    awsAccessKeyId: asHint(row.awsAccessKeyId ?? row.aws_access_key_id),
    awsSecretAccessKey: asHint(row.awsSecretAccessKey ?? row.aws_secret_access_key),
    awsSessionToken: asHint(row.awsSessionToken ?? row.aws_session_token),
    databricksToken: asHint(row.databricksToken ?? row.databricks_token),
    bearerToken: asHint(row.bearerToken ?? row.bearer_token),
  };
}

function asProvider(value: unknown): AiModelProvider {
  const allowed: AiModelProvider[] = [
    'sagemaker',
    'azure_ml',
    'vertex_ai',
    'huggingface',
    'mlflow',
    'databricks',
    'rest',
    'on_prem',
  ];
  return allowed.includes(value as AiModelProvider) ? (value as AiModelProvider) : 'rest';
}

function asStatus(value: unknown): AiModelStatus {
  if (value === 'draft' || value === 'registered' || value === 'deprecated') return value;
  return 'draft';
}

function asEnvironment(value: unknown): AiModelEnvironment {
  if (value === 'development' || value === 'staging' || value === 'production') return value;
  return 'development';
}

function asTask(value: unknown): AiModelTask {
  const allowed: AiModelTask[] = [
    'binary_classification',
    'multiclass',
    'regression',
    'anomaly_detection',
    'clustering',
    'forecasting',
  ];
  return allowed.includes(value as AiModelTask) ? (value as AiModelTask) : 'binary_classification';
}

function asModality(value: unknown): AiModelModality {
  const allowed: AiModelModality[] = ['tabular', 'text', 'image', 'audio', 'multimodal'];
  return allowed.includes(value as AiModelModality) ? (value as AiModelModality) : 'tabular';
}

function asInference(value: unknown): InferenceMode {
  if (value === 'realtime' || value === 'batch' || value === 'both') return value;
  return 'realtime';
}

function asPayloadFormat(value: unknown): AiModelPayloadFormat {
  if (
    value === 'raw' ||
    value === 'sagemaker_instances' ||
    value === 'vertex_instances' ||
    value === 'dataframe_records'
  ) {
    return value;
  }
  return 'raw';
}

function asDataType(value: unknown): AiModelDataType {
  const allowed: AiModelDataType[] = [
    'string',
    'number',
    'integer',
    'boolean',
    'category',
    'datetime',
    'array',
    'object',
  ];
  return allowed.includes(value as AiModelDataType) ? (value as AiModelDataType) : 'string';
}

function mapInput(raw: Record<string, unknown>): AiModelInputField {
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    dataType: asDataType(raw.dataType ?? raw.data_type),
    required: asBool(raw.required, true),
    nullable: asBool(raw.nullable),
    example: asString(raw.example),
  };
}

function mapOutput(raw: Record<string, unknown>): AiModelOutputField {
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    dataType: asDataType(raw.dataType ?? raw.data_type),
    scoreSupported: asBool(raw.scoreSupported ?? raw.score_supported),
  };
}

function mapLabel(raw: Record<string, unknown>): AiModelClassificationLabel {
  return {
    id: asString(raw.id),
    value: asString(raw.value),
    label: asString(raw.label),
  };
}

function mapCheck(raw: Record<string, unknown>): AiModelTestCheck {
  return {
    id: asString(raw.id),
    label: asString(raw.label),
    passed: asBool(raw.passed),
    detail: asString(raw.detail) || undefined,
  };
}

function mapLastTest(raw: unknown): AiModelTestResult | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const checks = Array.isArray(row.checks) ? row.checks.map((item) => mapCheck(item as Record<string, unknown>)) : [];
  return {
    ranAt: asString(row.ranAt ?? row.ran_at),
    success: asBool(row.success),
    latencyMs: asNumber(row.latencyMs ?? row.latency_ms, 0),
    checks,
    response: row.response,
  };
}

function mapModel(raw: Record<string, unknown>): AiRegisteredModel {
  const mask = mapCredentialsMask(raw.credentials);
  return {
    id: asString(raw.id),
    code: asString(raw.code),
    name: asString(raw.name),
    version: asString(raw.version),
    description: asString(raw.description),
    environment: asEnvironment(raw.environment),
    status: asStatus(raw.status),
    tags: asStringArray(raw.tags),
    owner: asString(raw.owner),
    provider: asProvider(raw.provider),
    endpointId: asString(raw.endpointId ?? raw.endpoint_id),
    location: asString(raw.location),
    connectionId: asString(raw.connectionId ?? raw.connection_id),
    authLabel: asString(raw.authLabel ?? raw.auth_label),
    httpMethod: (asString(raw.httpMethod ?? raw.http_method, 'POST') === 'GET' ? 'GET' : 'POST') as AiModelHttpMethod,
    contentType: asString(raw.contentType ?? raw.content_type, 'application/json'),
    payloadFormat: asPayloadFormat(raw.payloadFormat ?? raw.payload_format),
    timeoutMs: asNumber(raw.timeoutMs ?? raw.timeout_ms, 5000),
    retryCount: asNumber(raw.retryCount ?? raw.retry_count, 0),
    task: asTask(raw.task),
    modality: asModality(raw.modality),
    inputFields: Array.isArray(raw.inputFields)
      ? (raw.inputFields as Record<string, unknown>[]).map(mapInput)
      : Array.isArray(raw.input_fields)
        ? (raw.input_fields as Record<string, unknown>[]).map(mapInput)
        : [],
    outputFields: Array.isArray(raw.outputFields)
      ? (raw.outputFields as Record<string, unknown>[]).map(mapOutput)
      : Array.isArray(raw.output_fields)
        ? (raw.output_fields as Record<string, unknown>[]).map(mapOutput)
        : [],
    classificationLabels: Array.isArray(raw.classificationLabels)
      ? (raw.classificationLabels as Record<string, unknown>[]).map(mapLabel)
      : Array.isArray(raw.classification_labels)
        ? (raw.classification_labels as Record<string, unknown>[]).map(mapLabel)
        : [],
    preprocessingIncluded: asBool(raw.preprocessingIncluded ?? raw.preprocessing_included, true),
    preprocessingNotes: asString(raw.preprocessingNotes ?? raw.preprocessing_notes),
    inferenceMode: asInference(raw.inferenceMode ?? raw.inference_mode),
    sampleRequest: asString(raw.sampleRequest ?? raw.sample_request, '{}'),
    sampleResponse: asString(raw.sampleResponse ?? raw.sample_response, '{}'),
    framework: asString(raw.framework),
    algorithm: asString(raw.algorithm),
    components: asStringArray(raw.components),
    lastTest: mapLastTest(raw.lastTest ?? raw.last_test),
    credentialsConfigured: asBool(raw.credentialsConfigured ?? raw.credentials_configured) || mask.configured,
    credentials: mask,
    createdAt: asString(raw.createdAt ?? raw.created_at),
    updatedAt: asString(raw.updatedAt ?? raw.updated_at),
  };
}

function credentialsPayload(creds: AiModelCredentialsInput): Record<string, string> {
  return {
    hfToken: creds.hfToken.trim(),
    endpointUrl: creds.endpointUrl.trim(),
    apiKey: creds.apiKey.trim(),
    scoringUri: creds.scoringUri.trim(),
    deploymentName: creds.deploymentName.trim(),
    awsAccessKeyId: creds.awsAccessKeyId.trim(),
    awsSecretAccessKey: creds.awsSecretAccessKey.trim(),
    awsSessionToken: creds.awsSessionToken.trim(),
    databricksToken: creds.databricksToken.trim(),
    bearerToken: creds.bearerToken.trim(),
  };
}

export function formToPayload(form: AiModelFormState, status: AiModelStatus): Record<string, unknown> {
  return {
    name: form.name.trim(),
    code: form.code.trim(),
    version: form.version.trim(),
    description: form.description.trim(),
    environment: form.environment,
    status,
    tags: form.tags.map((tag) => tag.trim()).filter(Boolean),
    owner: form.owner.trim(),
    provider: form.provider,
    endpointId: form.endpointId.trim(),
    location: form.location.trim(),
    connectionId: form.connectionId.trim(),
    authLabel: form.authLabel.trim(),
    httpMethod: form.httpMethod,
    contentType: form.contentType.trim() || 'application/json',
    payloadFormat: form.payloadFormat,
    timeoutMs: form.timeoutMs,
    retryCount: form.retryCount,
    task: form.task,
    modality: form.modality,
    inputFields: form.inputFields
      .filter((field) => field.name.trim())
      .map((field) => ({
        id: field.id,
        name: field.name.trim(),
        dataType: field.dataType,
        required: field.required,
        nullable: field.nullable,
        example: field.example,
      })),
    outputFields: form.outputFields
      .filter((field) => field.name.trim())
      .map((field) => ({
        id: field.id,
        name: field.name.trim(),
        dataType: field.dataType,
        scoreSupported: field.scoreSupported,
      })),
    classificationLabels: form.classificationLabels
      .filter((row) => row.value.trim() || row.label.trim())
      .map((row) => ({ id: row.id, value: row.value.trim(), label: row.label.trim() })),
    preprocessingIncluded: form.preprocessingIncluded,
    preprocessingNotes: form.preprocessingNotes.trim(),
    inferenceMode: form.inferenceMode,
    sampleRequest: form.sampleRequest,
    sampleResponse: form.sampleResponse,
    framework: form.framework.trim(),
    algorithm: form.algorithm.trim(),
    components: form.components.map((item) => item.trim()).filter(Boolean),
    credentials: credentialsPayload(form.credentials),
  };
}

export const aiModelService = {
  async list(): Promise<AiRegisteredModel[]> {
    logAiModel('app', 'list_models');
    try {
      const data = (await apiService.get(BASE, {}, CONNECTION)) as Record<string, unknown>;
      const items = Array.isArray(data.items) ? data.items : [];
      const models = items.map((item) => mapModel(item as Record<string, unknown>));
      logAiModel('app', 'list_models_ok', { count: models.length });
      return models;
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'list_models_failed', { message });
      throw new Error(message);
    }
  },

  async getById(id: string): Promise<AiRegisteredModel> {
    logAiModel('app', 'get_model', { id });
    try {
      const data = (await apiService.get(`${BASE}/${id}`, {}, CONNECTION)) as Record<string, unknown>;
      const model = mapModel(data);
      logAiModel('app', 'get_model_ok', { id: model.id, code: model.code });
      return model;
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'get_model_failed', { message, id });
      throw new Error(message);
    }
  },

  async create(form: AiModelFormState, status: AiModelStatus): Promise<AiRegisteredModel> {
    logAiModel('app', 'create_model', { code: form.code, provider: form.provider, status });
    try {
      const data = (await apiService.post(BASE, formToPayload(form, status), CONNECTION)) as Record<string, unknown>;
      const model = mapModel(data);
      logAiModel('app', 'create_model_ok', { id: model.id, code: model.code });
      return model;
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'create_model_failed', { message, code: form.code });
      throw new Error(message);
    }
  },

  async update(id: string, form: AiModelFormState, status: AiModelStatus): Promise<AiRegisteredModel> {
    logAiModel('app', 'update_model', { id, code: form.code, status });
    try {
      const data = (await apiService.put(`${BASE}/${id}`, formToPayload(form, status), CONNECTION)) as Record<
        string,
        unknown
      >;
      const model = mapModel(data);
      logAiModel('app', 'update_model_ok', { id: model.id, code: model.code });
      return model;
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'update_model_failed', { message, id });
      throw new Error(message);
    }
  },

  async remove(id: string): Promise<void> {
    logAiModel('app', 'delete_model', { id });
    try {
      await apiService.delete(`${BASE}/${id}`, CONNECTION);
      logAiModel('app', 'delete_model_ok', { id });
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'delete_model_failed', { message, id });
      throw new Error(message);
    }
  },

  async testDraft(form: AiModelFormState): Promise<AiModelTestResult> {
    logAiModel('app', 'test_draft', { code: form.code, provider: form.provider });
    try {
      const data = (await apiService.post(
        `${BASE}/test-draft`,
        formToPayload(form, 'draft'),
        CONNECTION
      )) as Record<string, unknown>;
      const result = mapLastTest({
        ranAt: new Date().toISOString(),
        success: data.success,
        latencyMs: data.latencyMs ?? data.latency_ms,
        checks: data.checks,
        response: data.response,
      });
      if (!result) throw new Error('Test returned an empty result');
      logAiModel(result.success ? 'app' : 'warning', result.success ? 'test_draft_ok' : 'test_draft_incomplete', {
        code: form.code,
        success: result.success,
        latencyMs: result.latencyMs,
      });
      return result;
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'test_draft_failed', { message, code: form.code });
      throw new Error(message);
    }
  },

  async invoke(id: string, payload: Record<string, unknown>): Promise<{ success: boolean; latencyMs: number; response?: unknown }> {
    logAiModel('app', 'invoke_model', { id });
    try {
      const data = (await apiService.post(`${BASE}/${id}/invoke`, { payload }, CONNECTION)) as Record<
        string,
        unknown
      >;
      const latencyMs = asNumber(data.latencyMs ?? data.latency_ms, 0);
      logAiModel('app', 'invoke_model_ok', { id, latencyMs });
      return {
        success: asBool(data.success, true),
        latencyMs,
        response: data.response,
      };
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'invoke_model_failed', { message, id });
      throw new Error(message);
    }
  },

  async testSaved(id: string): Promise<AiModelTestResult> {
    logAiModel('app', 'test_saved', { id });
    try {
      const data = (await apiService.post(`${BASE}/${id}/test`, {}, CONNECTION)) as Record<string, unknown>;
      const result = mapLastTest({
        ranAt: new Date().toISOString(),
        success: data.success,
        latencyMs: data.latencyMs ?? data.latency_ms,
        checks: data.checks,
        response: data.response,
      });
      if (!result) throw new Error('Test returned an empty result');
      logAiModel(result.success ? 'app' : 'warning', result.success ? 'test_saved_ok' : 'test_saved_incomplete', {
        id,
        success: result.success,
        latencyMs: result.latencyMs,
      });
      return result;
    } catch (err) {
      const message = errorMessage(err);
      logAiModel('error', 'test_saved_failed', { message, id });
      throw new Error(message);
    }
  },
};
